/**
 * Promotion Module Types
 * Types for the promotion system including promotions, rules, application methods, and adjustments
 */

// ============================================================================
// Promotion Core Types
// ============================================================================

/**
 * Main promotion entity that defines discount rules and metadata
 */
export interface Promotion {
    id: string
    accountId: string
    code: string // Unique identifier within account, also serves as coupon code
    type: PromotionType
    isAutomatic: boolean // true = auto-apply, false = requires code
    isStackable: boolean // true = can be combined with other stackable promotions, false = cannot be combined
    campaignId?: string | null // Optional: Group promotions under campaigns
    name: string
    description?: string | null
    /** Public URL for badge/icon on storefront (e.g. GCS media) */
    badgeImageUrl?: string | null
    /** When true, storefront may show badge image on product cards */
    badgeShowOnProductCard?: boolean
    /** When true, storefront may show badge image on product detail */
    badgeShowOnProductDetail?: boolean
    status: PromotionStatus
    createdAt: Date
    updatedAt: Date
    deletedAt?: Date | null
}

export enum PromotionType {
    STANDARD = 'standard',
}

export enum PromotionStatus {
    ACTIVE = 'active',
    INACTIVE = 'inactive',
}

// ============================================================================
// Promotion Rule Types
// ============================================================================

/**
 * Reusable rule entity that can be attached to either Promotions (cart/order level)
 * or ApplicationMethods (target level)
 */
export interface PromotionRule {
    id: string
    accountId: string
    ruleAttribute: string // e.g., "subtotal", "item_count", "customer_id", "product_id", "category_id"
    ruleOperator: PromotionRuleOperator
    createdAt: Date
    updatedAt: Date
}

export enum PromotionRuleOperator {
    GT = 'gt', // greater than
    GTE = 'gte', // greater than or equal
    LT = 'lt', // less than
    LTE = 'lte', // less than or equal
    EQ = 'eq', // equals
    IN = 'in', // in array
    NIN = 'nin', // not in array
}

/**
 * Stores the values that a PromotionRule is evaluated against
 */
export interface PromotionRuleValue {
    id: string
    promotionRuleId: string
    value: string // Flexible string value (can represent numbers, UUIDs, etc.)
    createdAt: Date
    updatedAt: Date
}

// ============================================================================
// Pivot Tables - Promotion Rules
// ============================================================================

/**
 * Many-to-many relationship between Promotion and PromotionRule for cart/order-level rules
 */
export interface PromotionPromotionRule {
    promotionId: string
    promotionRuleId: string
}

/**
 * Many-to-many relationship between PromotionApplicationMethod and PromotionRule for target-level rules
 */
export interface PromotionApplicationMethodPromotionRule {
    applicationMethodId: string
    promotionRuleId: string
}

// ============================================================================
// Application Method Types
// ============================================================================

/**
 * Defines how the promotion discount is applied
 */
export interface PromotionApplicationMethod {
    id: string
    accountId: string
    promotionId: string // 1-to-1 relationship with Promotion
    targetType: PromotionTargetType
    applicationType: PromotionApplicationType
    value: number // Discount value (for percentage/fixed)
    /** For N_FOR_M: number of units to buy (e.g. 2 for 2x1, 3 for 3x2) */
    buyQuantity?: number | null
    /** For N_FOR_M: number of units to pay (e.g. 1 for 2x1, 2 for 3x2) */
    payQuantity?: number | null
    /** Optional: label for badge (e.g. "2x1", "20% OFF") */
    badgeLabel?: string | null
    /** Optional: badge type for preview (e.g. "n_for_m", "percentage") */
    badgeType?: string | null
    createdAt: Date
    updatedAt: Date
    deletedAt?: Date | null
}

export enum PromotionTargetType {
    ITEMS = 'items', // apply to cart/order line items
    SHIPPING = 'shipping', // apply to cart/order delivery methods
    ORDER = 'order', // apply to order total
}

export enum PromotionApplicationType {
    PERCENTAGE = 'percentage', // e.g., 10% off
    FIXED = 'fixed', // e.g., $5 off
    N_FOR_M = 'n_for_m', // e.g., 2x1 (buy 2 pay 1), 3x2 (buy 3 pay 2)
}

// ============================================================================
// Cart Adjustment Types
// ============================================================================

/**
 * Tracks promotion adjustments applied to individual cart items
 */
export interface CartLineItemAdjustment {
    id: string
    accountId: string
    cartItemId: string
    promotionId?: string | null
    description: string // e.g., "10% off Summer Sale"
    code?: string | null // Promotion code if applicable
    amount: number // Discount amount (always positive)
    createdAt: Date
    updatedAt: Date
    deletedAt?: Date | null
}

/**
 * Tracks promotion adjustments applied to cart delivery methods
 */
export interface CartDeliveryMethodAdjustment {
    id: string
    accountId: string
    cartDeliveryMethodId: string
    promotionId?: string | null
    description: string
    code?: string | null
    amount: number
    createdAt: Date
    updatedAt: Date
    deletedAt?: Date | null
}

/**
 * Links carts to applied promotions for easy querying (pivot table)
 */
export interface CartPromotion {
    cartId: string
    promotionId: string
    createdAt: Date
    updatedAt: Date
    deletedAt?: Date | null
}

// ============================================================================
// Order Adjustment Types
// ============================================================================

/**
 * Tracks promotion adjustments on order items (snapshot from cart)
 */
export interface OrderLineItemAdjustment {
    id: string
    accountId: string
    orderItemId: string
    promotionId?: string | null
    description: string
    code?: string | null
    amount: number
    createdAt: Date
    updatedAt: Date
    deletedAt?: Date | null
}

/**
 * Tracks promotion adjustments on order delivery methods
 */
export interface OrderDeliveryMethodAdjustment {
    id: string
    accountId: string
    orderDeliveryMethodId: string
    promotionId?: string | null
    description: string
    code?: string | null
    amount: number
    createdAt: Date
    updatedAt: Date
    deletedAt?: Date | null
}

/**
 * Links orders to applied promotions for easy querying (pivot table, snapshot from cart)
 */
export interface OrderPromotion {
    id: string
    orderId: string
    accountId: string
    promotionId: string
    code?: string | null
    name?: string | null
    amount: number
    isAutomatic: boolean
    createdAt: Date
    updatedAt: Date
    deletedAt?: Date | null
}

// ============================================================================
// DTOs y contratos de API/Front
// ============================================================================

export interface PromotionRuleInput {
    ruleAttribute: string
    ruleOperator: PromotionRuleOperator
    values: string[]
}

export interface PromotionApplicationMethodInput {
    targetType: PromotionTargetType
    applicationType: PromotionApplicationType
    value: number
    /** Required when applicationType is N_FOR_M: units to buy (e.g. 2 for 2x1) */
    buyQuantity?: number | null
    /** Required when applicationType is N_FOR_M: units to pay (e.g. 1 for 2x1) */
    payQuantity?: number | null
}

export interface CreatePromotionDTO {
    name: string
    description?: string | null
    code?: string | null
    isAutomatic: boolean
    isStackable?: boolean
    status: PromotionStatus
    type?: PromotionType
    campaignId?: string | null
    promotionRules?: PromotionRuleInput[]
    targetRules?: PromotionRuleInput[]
    applicationMethod: PromotionApplicationMethodInput
}

export interface UpdatePromotionDTO extends Partial<CreatePromotionDTO> {
    id: string
}

export interface PromotionListFilters {
    page?: number
    limit?: number
    search?: string | null
    status?: PromotionStatus | null
    isAutomatic?: boolean | null
}
